import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Modal, Form, Alert, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { apiGet, apiPost, apiPut, apiDelete, API_BASE_URL } from '../utils/apiConfig';

function PlantillasPage() {
  const navigate = useNavigate();
  
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroCategoria, setFiltroCategoria] = useState('todas');
  const [mensaje, setMensaje] = useState({ texto: '', tipo: '' });

  // ⭐ Modal formulario extra
  const [showModalFormulario, setShowModalFormulario] = useState(false);
  const [plantillaFormulario, setPlantillaFormulario] = useState(null);
  const [formularioExtra, setFormularioExtra] = useState({ activo: false, campos: [] });
  const [guardandoFormulario, setGuardandoFormulario] = useState(false);
  
  // Modal de crear/editar
  const [showModal, setShowModal] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [plantillaActual, setPlantillaActual] = useState(null);
  
  // Modal de variables
  const [showModalVariables, setShowModalVariables] = useState(false);
  const [variables, setVariables] = useState({});
  
  // Modal confirmar eliminación
  const [showModalEliminar, setShowModalEliminar] = useState(false);
  const [plantillaAEliminar, setPlantillaAEliminar] = useState(null);
  
  // Estados para subida de archivos
  const [archivoWord, setArchivoWord] = useState(null);
  const [subiendoArchivos, setSubiendoArchivos] = useState(false);
  const [archivosSubidos, setArchivosSubidos] = useState(false);
  
  // Formulario
  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    descripcion: '',
    categoria: 'medico',
    version: '1.0',
    archivoOriginal: null,
    variables: []
  });

  // ⭐ Orden de categorías para mostrar
  const ordenCategorias = ['paciente', 'firmante', 'representante', 'funcionario', 'documento', 'firmas'];

  useEffect(() => {
    cargarPlantillas();
  }, []);

  // =====================================================
  // CARGAR PLANTILLAS
  // =====================================================
  const cargarPlantillas = async () => {
    try {
      setLoading(true);
      const data = await apiGet('/api/plantillas');
      setPlantillas(data);
    } catch (error) {
      console.error('❌ Error cargando plantillas:', error);
      setMensaje({ texto: 'Error al cargar plantillas', tipo: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  const cargarVariablesDisponibles = async () => {
    try {
      const data = await apiGet('/api/plantillas/variables/disponibles');
      setVariables(data);
      setShowModalVariables(true);
    } catch (error) {
      console.error('❌ Error cargando variables:', error);
      setMensaje({ texto: 'Error al cargar variables', tipo: 'danger' });
    }
  };

  // =====================================================
  // MODAL CREAR / EDITAR
  // =====================================================
  const abrirModalCrear = () => {
    setModoEdicion(false);
    setPlantillaActual(null);
    setArchivoWord(null);
    setArchivosSubidos(false);
    setFormData({ codigo: '', nombre: '', descripcion: '', categoria: 'medico', version: '1.0', archivoOriginal: null, variables: [] });
    setShowModal(true);
  };

  const abrirModalEditar = (plantilla) => {
    setModoEdicion(true);
    setPlantillaActual(plantilla);
    setArchivoWord(null);
    setArchivosSubidos(!!plantilla.archivoOriginal);
    setFormData({
      codigo: plantilla.codigo,
      nombre: plantilla.nombre,
      descripcion: plantilla.descripcion || '',
      categoria: plantilla.categoria,
      version: plantilla.version || '1.0',
      archivoOriginal: plantilla.archivoOriginal || null,
      variables: plantilla.variables || []
    });
    setShowModal(true);
  };

  const handleWordChange = (e) => {
    const file = e.target.files[0];
    if (!file) { setArchivoWord(null); return; }
    if (!file.name.endsWith('.docx')) {
      setMensaje({ texto: '❌ Solo se permiten archivos .docx', tipo: 'danger' });
      setArchivoWord(null); return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMensaje({ texto: '❌ El archivo no debe superar 10MB', tipo: 'danger' });
      setArchivoWord(null); return;
    }
    setArchivoWord(file);
    setArchivosSubidos(false);
    setMensaje({ texto: '', tipo: '' });
    if (!formData.nombre) {
      const nombreSugerido = file.name.replace('.docx', '').replace(/_/g, ' ').replace(/-/g, ' ');
      setFormData(prev => ({ ...prev, nombre: nombreSugerido }));
    }
  };

  const procesarArchivo = async () => {
    if (!archivoWord) { setMensaje({ texto: '❌ Selecciona un archivo Word primero', tipo: 'danger' }); return; }
    setSubiendoArchivos(true);
    setMensaje({ texto: '⏳ Procesando archivo Word...', tipo: 'info' });
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('archivoWord', archivoWord);
      const response = await apiPost('/api/plantillas/upload-completa', formDataUpload, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFormData(prev => ({ ...prev, archivoOriginal: response.archivoOriginal, variables: response.variables || [] }));
      setArchivosSubidos(true);
      setMensaje({ texto: `✅ Archivo procesado. ${response.totalVariables || 0} variables detectadas.`, tipo: 'success' });
    } catch (error) {
      setMensaje({ texto: error.response?.data?.message || 'Error al procesar archivo', tipo: 'danger' });
    } finally {
      setSubiendoArchivos(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!modoEdicion && !formData.archivoOriginal) {
        setMensaje({ texto: '❌ Debes procesar el archivo Word primero', tipo: 'danger' }); return;
      }
      const datosParaEnviar = {
        codigo: formData.codigo, nombre: formData.nombre, descripcion: formData.descripcion,
        categoria: formData.categoria, version: formData.version, tipoArchivo: 'docx',
        archivoOriginal: formData.archivoOriginal, variables: formData.variables || []
      };
      if (modoEdicion) {
        await apiPut(`/api/plantillas/${plantillaActual._id}`, datosParaEnviar);
        setMensaje({ texto: '✅ Plantilla actualizada exitosamente', tipo: 'success' });
      } else {
        await apiPost('/api/plantillas', datosParaEnviar);
        setMensaje({ texto: '✅ Plantilla creada exitosamente', tipo: 'success' });
      }
      setShowModal(false);
      cargarPlantillas();
      setTimeout(() => setMensaje({ texto: '', tipo: '' }), 3000);
    } catch (error) {
      setMensaje({ texto: error.response?.data?.message || 'Error al guardar plantilla', tipo: 'danger' });
    }
  };

  const toggleActivo = async (plantilla) => {
    try {
      await apiPut(`/api/plantillas/${plantilla._id}`, { activo: !plantilla.activo });
      setMensaje({ texto: `✅ Plantilla ${plantilla.activo ? 'desactivada' : 'activada'}`, tipo: 'success' });
      cargarPlantillas();
      setTimeout(() => setMensaje({ texto: '', tipo: '' }), 3000);
    } catch (error) {
      setMensaje({ texto: 'Error al cambiar estado de la plantilla', tipo: 'danger' });
    }
  };

  const confirmarEliminar = (plantilla) => {
    setPlantillaAEliminar(plantilla);
    setShowModalEliminar(true);
  };

  const eliminarPlantillaPermanente = async () => {
    if (!plantillaAEliminar) return;
    try {
      await apiDelete(`/api/plantillas/${plantillaAEliminar._id}/permanente`);
      setMensaje({ texto: '✅ Plantilla eliminada permanentemente', tipo: 'success' });
      setShowModalEliminar(false);
      setPlantillaAEliminar(null);
      cargarPlantillas();
      setTimeout(() => setMensaje({ texto: '', tipo: '' }), 3000);
    } catch (error) {
      setMensaje({ texto: 'Error al eliminar plantilla', tipo: 'danger' });
    }
  };

  const copiarVariable = (variable) => {
    navigator.clipboard.writeText(variable);
    setMensaje({ texto: `✅ Variable ${variable} copiada al portapapeles`, tipo: 'success' });
    setTimeout(() => setMensaje({ texto: '', tipo: '' }), 2000);
  };

  // =====================================================
  // ⭐ FORMULARIO EXTRA
  // =====================================================
  const handleAbrirModalFormulario = (plantilla) => {
    setPlantillaFormulario(plantilla);
    setFormularioExtra(plantilla.formularioExtra || { activo: false, campos: [] });
    setShowModalFormulario(true);
  };

  const handleAgregarCampo = () => {
    setFormularioExtra(prev => ({
      ...prev,
      campos: [...prev.campos, {
        id: `campo_${Date.now()}`,
        tipo: 'checkbox',
        etiqueta: '',
        variableWord: '',
        requerido: false,
        placeholder: ''
      }]
    }));
  };

  const handleCampoChange = (index, field, value) => {
    setFormularioExtra(prev => {
      const nuevos = [...prev.campos];
      nuevos[index] = { ...nuevos[index], [field]: value };
      return { ...prev, campos: nuevos };
    });
  };

  const handleEliminarCampo = (index) => {
    setFormularioExtra(prev => ({
      ...prev,
      campos: prev.campos.filter((_, i) => i !== index)
    }));
  };

  const handleGuardarFormulario = async () => {
    if (!plantillaFormulario) return;
    setGuardandoFormulario(true);
    try {
      await apiPut(`/api/plantillas/${plantillaFormulario._id}/formulario-extra`, { formularioExtra });
      setMensaje({ texto: '✅ Formulario configurado correctamente', tipo: 'success' });
      setShowModalFormulario(false);
      cargarPlantillas();
      setTimeout(() => setMensaje({ texto: '', tipo: '' }), 3000);
    } catch (error) {
      setMensaje({ texto: 'Error al guardar formulario', tipo: 'danger' });
    } finally {
      setGuardandoFormulario(false);
    }
  };

  const plantillasFiltradas = plantillas.filter(p => {
    if (filtroCategoria === 'todas') return true;
    return p.categoria === filtroCategoria;
  });

  // =====================================================
  // RENDER
  // =====================================================
  return (
    <Layout>
      <Container fluid className="mt-4">
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2>📋 Gestión de Plantillas</h2>
                <p className="text-muted">Administra las plantillas de documentos del sistema</p>
              </div>
              <div className="d-flex gap-2">
                <Button variant="outline-info" onClick={() => navigate('/variables')}>🔤 Gestionar Variables</Button>
                <Button variant="primary" size="lg" onClick={abrirModalCrear}>➕ Nueva Plantilla</Button>
              </div>
            </div>
          </Col>
        </Row>

        {mensaje.texto && (
          <Alert variant={mensaje.tipo} dismissible onClose={() => setMensaje({ texto: '', tipo: '' })}>
            {mensaje.texto}
          </Alert>
        )}

        <Row className="mb-3">
          <Col md={6}>
            <Card>
              <Card.Body>
                <Form.Group>
                  <Form.Label>🔍 Filtrar por categoría:</Form.Label>
                  <Form.Select value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)}>
                    <option value="todas">Todas las categorías</option>
                    <option value="medico">Médicas</option>
                    <option value="consentimiento">Consentimiento</option>
                    <option value="administrativo">Administrativas</option>
                  </Form.Select>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="bg-light">
              <Card.Body>
                <h5>📊 Resumen</h5>
                <p className="mb-1">
                  <strong>Total:</strong> {plantillas.length} plantillas |{' '}
                  <strong>Activas:</strong> {plantillas.filter(p => p.activo !== false).length}
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card>
          <Card.Body>
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Cargando plantillas...</p>
              </div>
            ) : plantillasFiltradas.length === 0 ? (
              <div className="text-center py-5">
                <h4>📭 No hay plantillas</h4>
                <p className="text-muted">
                  {filtroCategoria === 'todas'
                    ? 'Crea tu primera plantilla haciendo clic en "Nueva Plantilla"'
                    : `No hay plantillas de tipo ${filtroCategoria}`}
                </p>
              </div>
            ) : (
              <Table responsive hover>
                <thead className="table-light">
                  <tr>
                    <th>Código</th>
                    <th>Nombre</th>
                    <th>Categoría</th>
                    <th>Estado</th>
                    <th>Firma</th>
                    <th>Formulario</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {plantillasFiltradas.map(plantilla => (
                    <tr key={plantilla._id}>
                      <td><code>{plantilla.codigo}</code></td>
                      <td>
                        <strong>{plantilla.nombre}</strong>
                        {plantilla.variables?.length > 0 && (
                          <div className="text-muted small">{plantilla.variables.length} variables</div>
                        )}
                      </td>
                      <td>
                        <Badge bg={
                          plantilla.categoria === 'medico' ? 'primary' :
                          plantilla.categoria === 'consentimiento' ? 'info' : 'secondary'
                        }>
                          {plantilla.categoria === 'medico' && '🏥 Médico'}
                          {plantilla.categoria === 'consentimiento' && '📝 Consentimiento'}
                          {plantilla.categoria === 'administrativo' && '📄 Administrativo'}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={plantilla.activo !== false ? 'success' : 'danger'}>
                          {plantilla.activo !== false ? '✅ Activa' : '❌ Inactiva'}
                        </Badge>
                      </td>
                      <td>
                        {plantilla.firmaConfig?.paciente?.habilitada || plantilla.firmaConfig?.funcionario?.habilitada ? (
                          <Badge bg="success">✍️ Configurada</Badge>
                        ) : (
                          <Badge bg="secondary">Sin config</Badge>
                        )}
                      </td>
                      {/* ⭐ Columna formulario extra */}
                      <td>
                        {plantilla.formularioExtra?.activo ? (
                          <Badge bg="warning" text="dark">📋 {plantilla.formularioExtra.campos?.length || 0} campos</Badge>
                        ) : (
                          <Badge bg="light" text="secondary">Sin formulario</Badge>
                        )}
                      </td>
                      <td>
                        <div className="d-flex gap-1 justify-content-center">
                          {/* Configurar firmas */}
                          <OverlayTrigger placement="top" overlay={<Tooltip>Configurar posición de firmas</Tooltip>}>
                            <Button variant="outline-warning" size="sm" onClick={() => navigate(`/plantillas/${plantilla._id}/editor`)}>✍️</Button>
                          </OverlayTrigger>

                          {/* ⭐ Formulario dinámico */}
                          <OverlayTrigger placement="top" overlay={<Tooltip>Configurar formulario dinámico</Tooltip>}>
                            <Button
                              variant={plantilla.formularioExtra?.activo ? 'warning' : 'outline-secondary'}
                              size="sm"
                              onClick={() => handleAbrirModalFormulario(plantilla)}
                            >
                              📋
                            </Button>
                          </OverlayTrigger>

                          {/* Editar */}
                          <OverlayTrigger placement="top" overlay={<Tooltip>Editar plantilla</Tooltip>}>
                            <Button variant="outline-primary" size="sm" onClick={() => abrirModalEditar(plantilla)}>✏️</Button>
                          </OverlayTrigger>

                          {/* Activar/Desactivar */}
                          <OverlayTrigger placement="top" overlay={<Tooltip>{plantilla.activo !== false ? 'Desactivar' : 'Activar'}</Tooltip>}>
                            <Button
                              variant={plantilla.activo !== false ? 'outline-secondary' : 'outline-success'}
                              size="sm"
                              onClick={() => toggleActivo(plantilla)}
                            >
                              {plantilla.activo !== false ? '🔴' : '🟢'}
                            </Button>
                          </OverlayTrigger>

                          {/* Eliminar permanentemente */}
                          <OverlayTrigger placement="top" overlay={<Tooltip>Eliminar permanentemente</Tooltip>}>
                            <Button variant="outline-danger" size="sm" onClick={() => confirmarEliminar(plantilla)}>🗑️</Button>
                          </OverlayTrigger>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* MODAL: CREAR / EDITAR PLANTILLA                                    */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{modoEdicion ? '✏️ Editar Plantilla' : '➕ Nueva Plantilla'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit}>
              <Card className="mb-4 border-primary">
                <Card.Header className="bg-primary text-white"><strong>📄 Archivo Word</strong></Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <strong>Selecciona el archivo Word (.docx)</strong>
                      <Badge bg="danger" className="ms-2">Obligatorio</Badge>
                    </Form.Label>
                    <Form.Control type="file" accept=".docx" onChange={handleWordChange} disabled={subiendoArchivos} />
                    <Form.Text className="text-muted">
                      El sistema detectará automáticamente las variables (@NombrePaciente@, @Cedula@, etc.)
                    </Form.Text>
                    {archivoWord && (
                      <Alert variant="info" className="mt-2 mb-0 py-2">📄 {archivoWord.name} ({(archivoWord.size / 1024).toFixed(1)} KB)</Alert>
                    )}
                    {formData.archivoOriginal && !archivoWord && (
                      <Alert variant="success" className="mt-2 mb-0 py-2">✅ Word configurado: {formData.archivoOriginal}</Alert>
                    )}
                  </Form.Group>
                  {(!modoEdicion || archivoWord) && (
                    <Button variant="info" onClick={procesarArchivo} disabled={!archivoWord || subiendoArchivos} className="w-100">
                      {subiendoArchivos ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Procesando...</> : '📤 Procesar Archivo'}
                    </Button>
                  )}
                  {archivosSubidos && formData.variables?.length > 0 && (
                    <Alert variant="success" className="mt-3 mb-0">
                      <strong>✅ Variables detectadas ({formData.variables.length}):</strong>
                      <div className="mt-2">
                        {formData.variables.map((v, i) => (<code key={i} className="me-2 bg-light p-1 rounded">{v}</code>))}
                      </div>
                    </Alert>
                  )}
                </Card.Body>
              </Card>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Código *</Form.Label>
                    <Form.Control type="text" value={formData.codigo} onChange={(e) => setFormData({...formData, codigo: e.target.value.toUpperCase()})} placeholder="Ej: VPCI-FOR-158" required disabled={modoEdicion} />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Categoría *</Form.Label>
                    <Form.Select value={formData.categoria} onChange={(e) => setFormData({...formData, categoria: e.target.value})} required>
                      <option value="medico">🏥 Médico</option>
                      <option value="consentimiento">📝 Consentimiento</option>
                      <option value="administrativo">📄 Administrativo</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Versión</Form.Label>
                    <Form.Control type="text" value={formData.version} onChange={(e) => setFormData({...formData, version: e.target.value})} placeholder="1.0" />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Nombre *</Form.Label>
                <Form.Control type="text" value={formData.nombre} onChange={(e) => setFormData({...formData, nombre: e.target.value})} placeholder="Ej: Consentimiento Informado Toma de Muestras" required />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Descripción</Form.Label>
                <Form.Control as="textarea" rows={2} value={formData.descripcion} onChange={(e) => setFormData({...formData, descripcion: e.target.value})} placeholder="Descripción breve de la plantilla..." />
              </Form.Group>

              <div className="d-grid gap-2">
                <Button variant="success" type="submit" size="lg" disabled={!modoEdicion && !archivosSubidos}>
                  {modoEdicion ? '💾 Actualizar Plantilla' : '➕ Crear Plantilla'}
                </Button>
                <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* MODAL: CONFIRMAR ELIMINACIÓN                                        */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <Modal show={showModalEliminar} onHide={() => setShowModalEliminar(false)} centered>
          <Modal.Header closeButton className="bg-danger text-white">
            <Modal.Title>⚠️ Confirmar Eliminación</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>¿Estás seguro de que deseas eliminar <strong>permanentemente</strong> esta plantilla?</p>
            {plantillaAEliminar && (
              <Alert variant="warning">
                <strong>Plantilla:</strong> {plantillaAEliminar.nombre}<br/>
                <strong>Código:</strong> {plantillaAEliminar.codigo}
              </Alert>
            )}
            <p className="text-danger mb-0"><strong>⚠️ Esta acción no se puede deshacer.</strong></p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModalEliminar(false)}>Cancelar</Button>
            <Button variant="danger" onClick={eliminarPlantillaPermanente}>🗑️ Eliminar Permanentemente</Button>
          </Modal.Footer>
        </Modal>

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* MODAL: VARIABLES DISPONIBLES                                        */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <Modal show={showModalVariables} onHide={() => setShowModalVariables(false)} size="xl">
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title>📝 Variables Disponibles para Plantillas</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <Alert variant="info">
              <strong>💡 Cómo usar:</strong> Escribe estas variables en tu documento Word.
              Al generar el documento, se reemplazarán automáticamente con los valores reales.
            </Alert>
            {ordenCategorias.filter(cat => variables[cat]).map((categoria) => {
              const vars = variables[categoria];
              return (
                <div key={categoria} className="mb-4">
                  <h5 className="text-primary mb-3" style={{ borderBottom: '2px solid #0d6efd', paddingBottom: '8px' }}>
                    {categoria === 'paciente' && '👤 Datos del Paciente'}
                    {categoria === 'funcionario' && '👨‍⚕️ Datos del Funcionario'}
                    {categoria === 'documento' && '📄 Datos del Documento'}
                    {categoria === 'representante' && '👨‍👩‍👧 Representante Legal (Solo Menores de 18 años)'}
                    {categoria === 'firmante' && '✍️ Variables de Firma (⭐ Recomendadas)'}
                    {categoria === 'firmas' && '🖼️ Marcadores de Imágenes de Firmas'}
                  </h5>
                  {categoria === 'firmante' && (
                    <Alert variant="success" className="py-2">
                      <small><strong>⭐ RECOMENDADAS para la última página:</strong> Estas variables automáticamente muestran los datos del <strong>paciente</strong> (si es mayor de edad) o del <strong>representante legal</strong> (si es menor de 18 años). ¡Úsalas para evitar errores!</small>
                    </Alert>
                  )}
                  {categoria === 'representante' && (
                    <Alert variant="warning" className="py-2">
                      <small><strong>⚠️ Solo para menores de 18 años:</strong> Estas variables solo se llenan si el paciente tiene menos de 18 años. Para pacientes mayores de edad, estas variables quedarán <strong>vacías</strong>.</small>
                    </Alert>
                  )}
                  <Table hover size="sm" bordered>
                    <thead className="table-light">
                      <tr>
                        <th style={{width: '280px'}}>Variable</th>
                        <th>Descripción</th>
                        <th style={{width: '80px'}} className="text-center">Copiar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vars.map((v, index) => (
                        <tr key={index} className={v.descripcion.includes('⭐') ? 'table-success' : ''}>
                          <td>
                            <code style={{fontSize: '13px', fontWeight: 'bold'}}>{v.variable}</code>
                            {v.descripcion.includes('⭐') && <Badge bg="success" className="ms-2" style={{fontSize: '9px'}}>Recomendada</Badge>}
                          </td>
                          <td style={{fontSize: '14px'}}>{v.descripcion}</td>
                          <td className="text-center">
                            <Button variant="outline-primary" size="sm" onClick={() => copiarVariable(v.variable)} title="Copiar al portapapeles">📋</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              );
            })}
            {Object.entries(variables).filter(([cat]) => !ordenCategorias.includes(cat)).map(([categoria, vars]) => (
              <div key={categoria} className="mb-4">
                <h5 className="text-secondary mb-3 text-capitalize">📌 {categoria}</h5>
                <Table hover size="sm" bordered>
                  <thead className="table-light">
                    <tr>
                      <th style={{width: '280px'}}>Variable</th>
                      <th>Descripción</th>
                      <th style={{width: '80px'}} className="text-center">Copiar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vars.map((v, index) => (
                      <tr key={index}>
                        <td><code style={{fontSize: '13px'}}>{v.variable}</code></td>
                        <td>{v.descripcion}</td>
                        <td className="text-center">
                          <Button variant="outline-primary" size="sm" onClick={() => copiarVariable(v.variable)}>📋</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ))}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModalVariables(false)}>Cerrar</Button>
          </Modal.Footer>
        </Modal>

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* ⭐ MODAL: CONFIGURAR FORMULARIO DINÁMICO                           */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <Modal show={showModalFormulario} onHide={() => setShowModalFormulario(false)} size="lg" centered>
          <Modal.Header closeButton style={{ background: '#fff3cd' }}>
            <Modal.Title>📋 Formulario Dinámico — {plantillaFormulario?.nombre}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="info" className="py-2" style={{ fontSize: 13 }}>
              💡 Cuando esté activo, antes de generar el documento el funcionario verá un formulario
              para llenar estos campos. Las variables se reemplazarán en el Word automáticamente.
            </Alert>

            {/* Toggle activar/desactivar */}
            <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded mb-4">
              <div>
                <strong>Activar formulario dinámico</strong>
                <div className="text-muted small">Al activar, se mostrará antes de generar el documento</div>
              </div>
              <Form.Check
                type="switch"
                id="toggle-formulario"
                checked={formularioExtra.activo}
                onChange={(e) => setFormularioExtra(prev => ({ ...prev, activo: e.target.checked }))}
                style={{ transform: 'scale(1.3)' }}
              />
            </div>

            {/* Lista de campos */}
            {formularioExtra.campos.length === 0 && (
              <div className="text-center py-3 text-muted">
                <div style={{ fontSize: 32 }}>📋</div>
                <p className="mt-2">No hay campos configurados. Agrega campos con el botón de abajo.</p>
              </div>
            )}

            {formularioExtra.campos.map((campo, index) => (
              <div key={campo.id} className="border rounded p-3 mb-3 bg-white shadow-sm">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong style={{ fontSize: 13, color: '#495057' }}>Campo #{index + 1}</strong>
                  <Button variant="outline-danger" size="sm" onClick={() => handleEliminarCampo(index)}>🗑️</Button>
                </div>
                <div className="row g-2">
                  <div className="col-md-4">
                    <Form.Label className="small fw-bold mb-1">Tipo</Form.Label>
                    <Form.Select size="sm" value={campo.tipo} onChange={(e) => handleCampoChange(index, 'tipo', e.target.value)}>
                      <option value="checkbox">☑ Checkbox (sí/no)</option>
                      <option value="fecha">📅 Fecha</option>
                      <option value="texto">✏️ Texto libre</option>
                    </Form.Select>
                  </div>
                  <div className="col-md-4">
                    <Form.Label className="small fw-bold mb-1">Etiqueta (lo que ve el funcionario)</Form.Label>
                    <Form.Control
                      size="sm"
                      value={campo.etiqueta}
                      onChange={(e) => handleCampoChange(index, 'etiqueta', e.target.value)}
                      placeholder="Ej: EKG"
                    />
                  </div>
                  <div className="col-md-4">
                    <Form.Label className="small fw-bold mb-1">Variable en el Word</Form.Label>
                    <input
                      list={`variables-list-${index}`}
                      size="sm"
                      className="form-control form-control-sm"
                      value={campo.variableWord}
                      onChange={(e) => handleCampoChange(index, 'variableWord', e.target.value)}
                      placeholder="Escribe para filtrar..."
                    />
                    <datalist id={`variables-list-${index}`}>
                      {(plantillaFormulario?.variables || []).map((v, i) => (
                        <option key={i} value={`@${v}@`} />
                      ))}
                    </datalist>
                    {campo.variableWord && (
                      <small className="text-success">✅ {campo.variableWord}</small>
                    )}
                    {campo.variableWord && (
                      <small className="text-success">✅ {campo.variableWord}</small>
                    )}
                  </div>
                  {campo.tipo === 'texto' && (
                    <div className="col-md-8">
                      <Form.Label className="small fw-bold mb-1">Placeholder (texto de ayuda)</Form.Label>
                      <Form.Control
                        size="sm"
                        value={campo.placeholder}
                        onChange={(e) => handleCampoChange(index, 'placeholder', e.target.value)}
                        placeholder="Ej: herida quirúrgica, catéter..."
                      />
                    </div>
                  )}
                  <div className={campo.tipo === 'texto' ? 'col-md-4 d-flex align-items-end' : 'col-md-12 d-flex align-items-center'}>
                    <Form.Check
                      type="checkbox"
                      label="Campo requerido"
                      checked={campo.requerido}
                      onChange={(e) => handleCampoChange(index, 'requerido', e.target.checked)}
                    />
                  </div>
                </div>
              </div>
            ))}

            <Button variant="outline-primary" className="w-100 mt-2" onClick={handleAgregarCampo}>
              ➕ Agregar Campo
            </Button>

            {/* Ayuda de variables para este formulario */}
            {formularioExtra.campos.length > 0 && (
              <Alert variant="light" className="mt-3 border" style={{ fontSize: 12 }}>
                <strong>📌 Variables configuradas en este formulario:</strong>
                <div className="mt-1 d-flex flex-wrap gap-1">
                  {formularioExtra.campos.filter(c => c.variableWord).map((c, i) => (
                    <code key={i} className="bg-white border px-2 py-1 rounded">{c.variableWord}</code>
                  ))}
                </div>
                <div className="mt-2 text-muted">Asegúrate de que estas variables estén en el Word con exactamente el mismo nombre.</div>
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModalFormulario(false)}>Cancelar</Button>
            <Button variant="warning" onClick={handleGuardarFormulario} disabled={guardandoFormulario}>
              {guardandoFormulario
                ? <><span className="spinner-border spinner-border-sm me-2"></span>Guardando...</>
                : '💾 Guardar Configuración'}
            </Button>
          </Modal.Footer>
        </Modal>

      </Container>
    </Layout>
  );
}

export default PlantillasPage;